import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { keepPreviousData } from '@tanstack/react-query';
import {
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type Row,
  type RowSelectionState,
} from '@tanstack/react-table';
import { AlertCircle, Check, ExternalLink, RefreshCw, Search, Send, X } from 'lucide-react';
import { format } from 'date-fns';

import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/ui/data-table';
import { DataTableSkeleton } from '@/components/ui/data-table-skeleton';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScoutPostingStatusBadge } from '@/components/scout/ScoutStatusBadge';
import { ScoutPostingSheet } from '@/components/scout/ScoutPostingSheet';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  usePromoteScoutPostings,
  useReviewScoutPostings,
  useScoutPostings,
  useScoutPrograms,
  useScoutRuns,
} from '@/hooks/useScout';
import type { ScoutPosting, ScoutPostingStatus, ScoutRunSummary } from '@repo/types';

export const Route = createFileRoute('/admin/scout/postings')({
  component: ScoutPostingsPage,
  validateSearch: (search: Record<string, unknown>): { runId?: string } => ({
    runId: typeof search.runId === 'string' ? search.runId : undefined,
  }),
});

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'new', label: 'Awaiting review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All statuses' },
];

function ScoutPostingsPage() {
  const { runId } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const [search, setSearch] = useState('');
  // Defaults to the review queue: this page exists to action new postings, and
  // opening on "all" would bury them under everything already decided.
  const [statusFilter, setStatusFilter] = useState<string>('new');
  const [programFilter, setProgramFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [openPostingId, setOpenPostingId] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(search, 300);

  const { data: programs } = useScoutPrograms();
  // Enough runs to cover a working session; the selector is for "the crawl I
  // just ran", not an archive browser — Scout → Runs is that.
  const { data: runs } = useScoutRuns(undefined, { page: 1, limit: 25 });
  const scopedRun = useMemo(
    () => (runId ? runs?.items.find((run) => run.id === runId) : undefined),
    [runId, runs]
  );
  const runById = useMemo(() => {
    const map = new Map<string, ScoutRunSummary>();
    for (const run of runs?.items ?? []) map.set(run.id, run);
    return map;
  }, [runs]);

  const filters = useMemo(
    () => ({
      runId,
      search: debouncedSearch || undefined,
      status: statusFilter !== 'all' ? (statusFilter as ScoutPostingStatus) : undefined,
      programId: programFilter !== 'all' ? programFilter : undefined,
    }),
    [runId, debouncedSearch, statusFilter, programFilter]
  );

  const { data, isLoading, isFetching, error, refetch } = useScoutPostings(
    filters,
    { page, limit: pageSize },
    { placeholderData: keepPreviousData }
  );

  const reviewMutation = useReviewScoutPostings();
  const promoteMutation = usePromoteScoutPostings();

  const postings = useMemo(() => data?.items ?? [], [data]);

  // Selection is keyed by posting id, but it stops meaning anything once the
  // visible page changes. Clear it rather than carrying hidden selections into
  // a bulk action.
  useEffect(() => {
    setRowSelection({});
  }, [page, pageSize, statusFilter, programFilter, debouncedSearch, runId]);

  const columns = useMemo<ColumnDef<ScoutPosting>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all postings on this page"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={`Select ${row.original.title}`}
          />
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'title',
        header: 'Posting',
        cell: ({ row }) => {
          const posting = row.original;
          return (
            <div className="min-w-0 space-y-0.5">
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setOpenPostingId(posting.id)}
                  className="max-w-[340px] truncate text-left font-medium hover:underline"
                  title={posting.title}
                >
                  {posting.title}
                </button>
                <a
                  href={posting.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label="Open the posting on the career site"
                >
                  <ExternalLink className="size-3" />
                </a>
              </div>
              <p className="truncate text-xs text-muted-foreground">
                {[posting.company || posting.targetCompany, posting.location]
                  .filter(Boolean)
                  .join(' · ') || '—'}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: 'jobType',
        header: 'Type',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {[row.original.jobType, row.original.workMode].filter(Boolean).join(' / ') || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'postedAt',
        header: 'Posted',
        cell: ({ row }) => (
          // Kept exactly as the site wrote it ("3 days ago"): normalising a
          // relative date at scrape time would bake in the crawl timestamp.
          <span className="text-sm text-muted-foreground">{row.original.postedAt || '—'}</span>
        ),
      },
      // Which crawl produced this row, and — the part that matters — what it
      // was looking for.
      //
      // Without it the queue is a mixture: an `sde-1` result sits next to a
      // `social media manager` result, and approving or rejecting means judging
      // a posting against a criterion the row does not state. Redundant once the
      // queue is scoped to a single run, so it is dropped in that case.
      ...(runId
        ? []
        : [
            {
              id: 'run',
              header: 'Run',
              cell: ({ row }: { row: Row<ScoutPosting> }) => {
                const run = runById.get(row.original.runId);
                if (!run) {
                  return <span className="text-sm text-muted-foreground">—</span>;
                }
                return (
                  <button
                    type="button"
                    onClick={() => {
                      navigate({ search: { runId: run.id } });
                      resetPage();
                    }}
                    className="min-w-0 space-y-0.5 text-left"
                    title="Show only this run"
                  >
                    <p className="text-sm hover:underline">
                      {format(new Date(run.startedAt), 'd MMM, HH:mm')}
                    </p>
                    <p className="max-w-[150px] truncate text-xs text-muted-foreground">
                      {run.roleFilter || 'no role filter'}
                    </p>
                  </button>
                );
              },
            } satisfies ColumnDef<ScoutPosting>,
          ]),
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <ScoutPostingStatusBadge
            status={row.original.status}
            promoted={Boolean(row.original.promotedAt)}
          />
        ),
      },
      {
        accessorKey: 'scoutedAt',
        header: 'Scouted',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {format(new Date(row.original.scoutedAt), 'd MMM, HH:mm')}
          </span>
        ),
      },
    ],
    [runId, runById, navigate]
  );

  const table = useReactTable({
    data: postings,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setRowSelection,
    state: { rowSelection },
    getRowId: (row) => row.id,
  });

  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
  const selectedPostings = postings.filter((p) => selectedIds.includes(p.id));
  // Only unpromoted rows can be reviewed — a posting already handed to the
  // fetcher is history, and re-reviewing it would misrepresent what was sent.
  const reviewableIds = selectedPostings.filter((p) => !p.promotedAt).map((p) => p.id);
  const promotableIds = selectedPostings
    .filter((p) => p.status === 'approved' && !p.promotedAt)
    .map((p) => p.id);

  const busy = reviewMutation.isPending || promoteMutation.isPending;

  const review = (status: 'approved' | 'rejected') => {
    if (!reviewableIds.length) return;
    reviewMutation.mutate({ postingIds: reviewableIds, status }, { onSuccess: () => setRowSelection({}) });
  };

  const promote = () => {
    if (!promotableIds.length) return;
    promoteMutation.mutate({ postingIds: promotableIds }, { onSuccess: () => setRowSelection({}) });
  };

  const total = data?.total ?? 0;
  const totalPages = total ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const rangeStart = total ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(page * pageSize, total);

  const resetPage = () => setPage(1);

  return (
    <div className="space-y-6 p-4 duration-500 ease-out animate-in fade-in slide-in-from-bottom-6 sm:p-6">
      <PageHeader
        title="Scout Postings"
        description="Review what the Job Scout extension found. Approved postings are sent to the scraping queue, where the fetcher extracts them properly."
        actions={
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={isFetching ? 'size-4 animate-spin' : 'size-4'} />
            Refresh
          </Button>
        }
      />

      {runId && (
        <Alert>
          <AlertCircle className="size-4" />
          <AlertDescription>
            {/* Naming the run is the point: its role filter is the question
                these postings answer, and judging them against the wrong one is
                exactly the mistake an unlabelled scope invites. */}
            Showing one run
            {scopedRun && (
              <>
                {' '}
                — {format(new Date(scopedRun.startedAt), 'd MMM yyyy, HH:mm')}
                {scopedRun.roleFilter ? `, role “${scopedRun.roleFilter}”` : ', no role filter'}
              </>
            )}
            .{' '}
            <button
              type="button"
              onClick={() => {
                navigate({ search: {} });
                resetPage();
              }}
              className="font-medium underline"
            >
              Show all runs
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by title or company…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPage();
            }}
            className="pl-9"
          />
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          {/*
            Scope the queue to one crawl.
            One flat list mixes runs that asked different questions — a run for
            "sde-1" and a run for "social media manager" produce postings that
            cannot sensibly be triaged against each other, and nothing in a row
            says which run it came from. The run is the unit of work, so it is
            the unit of review.

            Backed by the URL param rather than local state, so the Runs page's
            posting-count link and this selector are the same mechanism, and a
            scoped queue survives a reload or a shared link.
          */}
          <Select
            value={runId ?? 'all'}
            onValueChange={(value) => {
              navigate({ search: value === 'all' ? {} : { runId: value } });
              resetPage();
            }}
          >
            <SelectTrigger className="min-w-0 flex-1 sm:w-[230px] sm:flex-none">
              <SelectValue placeholder="All runs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All runs</SelectItem>
              {(runs?.items ?? []).map((run) => (
                <SelectItem key={run.id} value={run.id}>
                  {format(new Date(run.startedAt), 'd MMM, HH:mm')}
                  {run.roleFilter ? ` · ${run.roleFilter}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={programFilter}
            onValueChange={(value) => {
              setProgramFilter(value);
              resetPage();
            }}
          >
            <SelectTrigger className="min-w-0 flex-1 sm:w-[200px] sm:flex-none">
              <SelectValue placeholder="All programs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All programs</SelectItem>
              {(programs ?? []).map((program) => (
                <SelectItem key={program.id} value={program.id}>
                  {program.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value);
              resetPage();
            }}
          >
            <SelectTrigger className="min-w-0 flex-1 sm:w-[170px] sm:flex-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bulk actions — only present when there is a selection to act on. */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 px-4 py-2.5 duration-200 animate-in fade-in slide-in-from-top-1">
          <span className="text-sm font-medium">
            {selectedIds.length} selected
          </span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => review('approved')} disabled={busy || !reviewableIds.length}>
              <Check className="size-4" />
              Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => review('rejected')} disabled={busy || !reviewableIds.length}>
              <X className="size-4" />
              Reject
            </Button>
            <Button size="sm" onClick={promote} disabled={busy || !promotableIds.length}>
              <Send className="size-4" />
              Send to queue{promotableIds.length ? ` (${promotableIds.length})` : ''}
            </Button>
          </div>
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>{error.message || 'Failed to load scout postings.'}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <DataTableSkeleton columns={7} />
      ) : (
        <>
          <DataTable table={table} />

          {total > 0 && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Showing <span className="font-medium text-foreground">{rangeStart}</span>–
                <span className="font-medium text-foreground">{rangeEnd}</span> of{' '}
                <span className="font-medium text-foreground">{total}</span> postings
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rows</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(value) => {
                      setPageSize(Number(value));
                      resetPage();
                    }}
                  >
                    <SelectTrigger className="h-8 w-[72px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[20, 50, 100].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <ScoutPostingSheet
        postingId={openPostingId}
        onOpenChange={(open) => {
          if (!open) setOpenPostingId(null);
        }}
      />
    </div>
  );
}
